// Shared helper function for uploading user profile images to Firebase Storage
// Uses Firebase Storage SDK (uploadBytes + getDownloadURL). No raw fetch or precondition headers.
// Unique path per upload to avoid 412 Precondition Failed on overwrite.

import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebaseConfig.js";

const BUCKET = typeof storage?.app?.options?.storageBucket === "string"
  ? storage.app.options.storageBucket
  : "";

/**
 * Log failed upload response when SDK throws (we don't have res; error may contain serverResponse).
 * If status is 412 and body mentions App Check, see src/lib/firebase/appCheck.ts for unblock steps
 * (disable Storage enforcement in Firebase Console or configure ReCaptcha v3 + env keys).
 */
function logUploadFailure(err, path) {
  const e = err && typeof err === "object" ? err : {};
  const status = e.customData?.status ?? e.status ?? "unknown";
  const body = typeof e.serverResponse === "string"
    ? e.serverResponse.slice(0, 2000)
    : (e.serverResponse != null ? String(e.serverResponse).slice(0, 2000) : "");
  const url = BUCKET
    ? `https://firebasestorage.googleapis.com/v0/b/${BUCKET}/o?name=${encodeURIComponent(path)}`
    : "(bucket unknown)";
  console.error("[StorageUpload] status", status, "body", body, "url", url, "headers", "[redacted]");
}

/**
 * Uploads a user image (profile picture, logo, or banner) to Firebase Storage.
 * Uses a unique path per upload to avoid 412 from overwrite preconditions.
 * @param {File} file - The image file to upload
 * @param {string} userId - The user's UID
 * @param {"profile" | "logo" | "banner"} kind - Type of image
 * @returns {Promise<string>} The download URL of the uploaded image
 */
export async function uploadUserImage(file, userId, kind) {
  if (!file) {
    throw new Error("No file provided");
  }
  if (!userId) {
    throw new Error("No user ID provided");
  }
  if (kind !== "profile" && kind !== "logo" && kind !== "banner") {
    throw new Error("Kind must be 'profile', 'logo', or 'banner'");
  }

  const maxSize = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSize) {
    throw new Error("Image size must be less than 5MB");
  }

  const extension = file.name.split(".").pop()?.toLowerCase() || "jpg";
  // Unique path to avoid overwrite and 412 precondition logic (no If-Match / ifGenerationMatch).
  const path = `uploads/${userId}/${kind}_${Date.now()}.${extension}`;
  const storageRef = ref(storage, path);
  const contentType = file.type || (extension === "png" ? "image/png" : "image/jpeg");

  try {
    await uploadBytes(storageRef, file, { contentType });
    const url = await getDownloadURL(storageRef);
    if (process.env.NODE_ENV === "development") {
      console.log("[StorageUpload] success", path, url.slice(0, 80) + (url.length > 80 ? "..." : ""));
    }
    return url;
  } catch (err) {
    logUploadFailure(err, path);
    throw err;
  }
}
